import axios, { AxiosError, AxiosInstance } from 'axios';
import {
  InterestForm,
  InitData,
  CustomersProduct,
  MunicipalitiesInterestForm,
  ContactFormData,
  CustomerFeedbackForm,
  SearchResult,
  SuggestionForm,
  SupplierOfferForm,
  SupplierRecommendationForm
} from '../models/customersTypes';

const blankToNull = <T extends object>(form: T) =>
  Object.fromEntries(
    Object.entries(form).map(([key, value]) => [key, typeof value === 'string' && !value.trim() ? null : value])
  );


// Base API configuration
const API_URL = import.meta.env.VITE_APP_API_URL || 'http://localhost:8000/api/v1';

interface ProductQuery {
  tags?: string;
  store?: number;
  q?: string;
  cat?: number;
  p?: number;
  page_size?: number;
}

// Send interest form
export const interestApi = {
  sendInterestForm: async (formData: InterestForm) => {
    try {
      const payload = {
        ...formData,
        trade_type: formData.trade_type === 'Händler' ? 'vendor' : 'direct_seller',
      };
      const response = await axiosInstance.post('/web/partners/interest', payload);
      return response.data;
    } catch (error) {
      console.error('Error sending interest form:', error);
      throw error;
    }
  },
  sendMunicipalitiesForm: async (formData: MunicipalitiesInterestForm) => {
    try {
      const response = await axiosInstance.post('/web/municipalities/interest', formData);
      return response.data;
    } catch (error) {
      console.error('Error sending interest form:', error);
      throw error;
    }
  },
  sendContactForm: async (formData: ContactFormData) => {
    try {
      const response = await axiosInstance.post('/web/contact', formData);
      return response.data;
    } catch (error) {
      console.error('Error sending interest form:', error);
      throw error;
    }
  },
  sendFeedback: async (formData: CustomerFeedbackForm) => {
    const response = await axiosInstance.post('/web/feedback', blankToNull(formData));
    return response.data;
  },
  sendSupplierOffer: async (formData: SupplierOfferForm) => {
    const response = await axiosInstance.post('/web/suppliers/offer', blankToNull(formData));
    return response.data;
  },
  sendSupplierRecommendation: async (formData: SupplierRecommendationForm) => {
    const response = await axiosInstance.post('/web/suppliers/recommendation', blankToNull(formData));
    return response.data;
  },
  sendSuggestionForm: async (formData: SuggestionForm) => {
    try {
      const response = await axiosInstance.post('/web/suggest-location', formData);
      return response.data;
    } catch (error) {
      console.error('Error sending interest form:', error);
      throw error;
    }
  }
};

export const customersProductsApi = {
  getInitData: async (): Promise<InitData> => {
    return apiGet<InitData>('/web/init');
  },

  getProducts: async (params: ProductQuery): Promise<SearchResult> => {
    return apiGet('/web/products/', params);
  },

  getById: async (id: number): Promise<CustomersProduct | undefined> => {
    return apiGet(`/web/products/${id}`);
  },
};

// Create axios instance with default config
const axiosInstance: AxiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 300000,
});

// Add authentication token to requests
axiosInstance.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token && config.headers) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

// Handle response errors
axiosInstance.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    // Handle 401 unauthorized errors (expired token, etc.)
    if (error.response?.status === 401) {
      localStorage.removeItem('auth_token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Generic API method to fetch data
const apiGet = async <T>(url: string, params?: object): Promise<T> => {
  try {
    const response = await axiosInstance.get<T>(url, { params });
    return response.data;
  } catch (error) {
    console.error(`Error fetching from ${url}:`, error);
    throw error;
  }
};

// Generic API method to post data
// const apiPost = async <T>(url: string, data: any): Promise<T> => {
//   try {
//     const response = await axiosInstance.post<T>(url, data);
//     return response.data;
//   } catch (error) {
//     console.error(`Error posting to ${url}:`, error);
//     throw error;
//   }
// };

export default {
  interests: interestApi,
  customers: customersProductsApi
};
